import { ValueCondition, ValueConditionOperator, StatusColor, StatusIndicatorConfig, LegacyStatusRange } from './types'

const toNumber = (v: unknown): number | undefined => {
  if (typeof v === 'number' && !Number.isNaN(v)) return v
  if (typeof v === 'string') {
    const trimmed = v.trim()
    if (trimmed === '') return undefined
    const n = Number(trimmed)
    if (!Number.isNaN(n)) return n
  }
  return undefined
}

export const evalValueCondition = (value: unknown, cond: ValueCondition): boolean => {
  const op = (cond as { operator: ValueConditionOperator }).operator
  const right = (cond as { value: unknown }).value
  switch (op) {
    case 'lt': {
      const l = toNumber(value)
      const r = toNumber(right)
      return l !== undefined && r !== undefined && l < r
    }
    case 'lte': {
      const l = toNumber(value)
      const r = toNumber(right)
      return l !== undefined && r !== undefined && l <= r
    }
    case 'gt': {
      const l = toNumber(value)
      const r = toNumber(right)
      return l !== undefined && r !== undefined && l > r
    }
    case 'gte': {
      const l = toNumber(value)
      const r = toNumber(right)
      return l !== undefined && r !== undefined && l >= r
    }
    case 'eq':
      return value === right
    case 'neq':
      return value !== right
    case 'in':
      return Array.isArray(right) && (right as unknown[]).some((v) => v === value)
    case 'notIn':
      return Array.isArray(right) && !(right as unknown[]).some((v) => v === value)
    case 'contains':
      return typeof value === 'string' && typeof right === 'string' && value.includes(right)
    case 'startsWith':
      return typeof value === 'string' && typeof right === 'string' && value.startsWith(right)
    case 'endsWith':
      return typeof value === 'string' && typeof right === 'string' && value.endsWith(right)
    case 'regex':
      try {
        const re = new RegExp(String(right))
        return typeof value === 'string' && re.test(value)
      } catch {
        return false
      }
    default:
      return false
  }
}

export const statusColorMap: Record<StatusColor, string> = {
  red: 'bg-gradient-to-r from-transparent to-red-100 border-red-500',
  amber: 'bg-gradient-to-r from-transparent to-amber-100 border-amber-500',
  green: 'bg-gradient-to-r from-transparent to-green-100 border-green-500',
}

export const computeStatusColorForValue = (
  value: unknown,
  cfg?: StatusIndicatorConfig,
  legacy?: LegacyStatusRange[]
): StatusColor | undefined => {
  if (cfg?.rules && cfg.rules.length > 0) {
    for (const rule of cfg.rules) {
      const anyOk = rule.anyOf?.some((c) => evalValueCondition(value, c)) ?? false
      const allOk = rule.allOf ? rule.allOf.every((c) => evalValueCondition(value, c)) : false
      if (anyOk || allOk) return rule.color
    }
  }
  if (legacy && typeof value === 'number' && !Number.isNaN(value)) {
    const hit = legacy.find((s) => s.lower <= value && s.upper >= value)
    return hit?.color
  }
  return undefined
}

//statusIndicator example usage:
//
// Numeric field, anyOf and allOf rules (operators: lt, lte, gt, gte, eq, neq, in, notIn):
//
// {
//   "statusIndicator": {
//     "rules": [
//       { "anyOf": [ { "operator": "lt", "value": 12 } ], "color": "red" },
//       { "allOf": [ { "operator": "gte", "value": 12.001 }, { "operator": "lte", "value": 22 } ], "color": "amber" },
//       { "anyOf": [ { "operator": "gt", "value": 22 } ], "color": "green" }
//     ]
//   }
// }

// String field, anyOf rules (operators: startsWith, endsWith, contains, regex):
//
// {
//   "statusIndicator": {
//     "rules": [
//       { "anyOf": [ { "operator": "startsWith", "value": "ERR" } ], "color": "red" },
//       { "anyOf": [ { "operator": "regex", "value": "^[A-Z]{3}-\\d+$" } ], "color": "amber" }
//     ]
//   }
// }
